import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import DatePicker from '@/components/DatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JUDGE_LEVELS = [
  'Club Judge',
  'Regional Judge',
  'National Judge',
  'Brevet Judge'
];

export default function JudgesTab() {
  const [judges, setJudges] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMissingInfoDialog, setShowMissingInfoDialog] = useState(false);
  const [judgeMissingInfo, setJudgeMissingInfo] = useState(null);
  const [judgeToDelete, setJudgeToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    bgNumber: '',
    level: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    loadJudges();
  }, []);

  const loadJudges = async () => {
    try {
      const response = await axios.get(`${API}/judges`);
      setJudges(response.data);
      
      // Check for judges missing contact info (phone or email)
      const missingInfo = response.data.find(j => !j.phone || !j.email);
      if (missingInfo) {
        setJudgeMissingInfo(missingInfo);
        setShowMissingInfoDialog(true);
      }
    } catch (error) {
      console.error('Failed to load judges:', error);
      toast.error('Failed to load judges');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLevelChange = (value) => {
    setFormData({ ...formData, level: value });
  };

  const handleOpenDialog = (judge = null) => {
    if (judge) {
      setEditing(judge);
      setFormData({
        firstName: judge.firstName,
        lastName: judge.lastName,
        dob: judge.dob,
        bgNumber: judge.bgNumber,
        level: judge.level,
        phone: judge.phone || '',
        email: judge.email || ''
      });
    } else {
      setEditing(null);
      setFormData({
        firstName: '',
        lastName: '',
        dob: '',
        bgNumber: '',
        level: '',
        phone: '',
        email: ''
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Manual validation
    if (!formData.firstName || !formData.lastName || !formData.dob || !formData.level || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields including contact number and email');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/judges/${editing.id}`, formData);
        toast.success('Judge updated successfully');
      } else {
        await axios.post(`${API}/judges`, formData);
        toast.success('Judge added successfully');
      }
      setShowDialog(false);
      loadJudges();
    } catch (error) {
      console.error('Failed to save judge:', error);
      toast.error('Failed to save judge');
    }
  };

  const handleDelete = async (judge) => {
    setJudgeToDelete(judge);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!judgeToDelete) return;

    try {
      await axios.delete(`${API}/judges/${judgeToDelete.id}`);
      toast.success(`${judgeToDelete.firstName} ${judgeToDelete.lastName}'s profile deleted successfully`);
      loadJudges();
    } catch (error) {
      console.error('Failed to delete judge:', error);
      toast.error('Failed to delete judge');
    } finally {
      setShowDeleteDialog(false);
      setJudgeToDelete(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Judges</h2>
          <p className="text-gray-600 text-sm mt-1">Manage your club's judges</p>
        </div>
        <Button
          data-testid="add-judge-button"
          onClick={() => handleOpenDialog()}
          className="bg-[#000000] hover:bg-[#333333]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Judge
        </Button>
      </div>

      {judges.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No judges added yet. Click "Add Judge" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {judges.map(judge => (
            <Card key={judge.id} data-testid={`judge-card-${judge.id}`} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{judge.firstName} {judge.lastName}</CardTitle>
                <CardDescription>Level: {judge.level}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">DOB:</span>
                    <span className="font-medium">{judge.dob ? format(new Date(judge.dob), 'dd/MM/yyyy') : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">BG Number:</span>
                    <span className="font-medium">{judge.bgNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className={`font-medium ${!judge.phone ? 'text-red-500' : ''}`}>{judge.phone || 'Missing!'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className={`font-medium ${!judge.email ? 'text-red-500' : ''} truncate max-w-[150px]`} title={judge.email}>{judge.email || 'Missing!'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-judge-button-${judge.id}`}
                    onClick={() => handleOpenDialog(judge)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    data-testid={`delete-judge-button-${judge.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(judge);
                    }}
                    size="sm"
                    variant="outline"
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Judge' : 'Add Judge'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update judge details' : 'Add a new judge to your club'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                data-testid="judge-first-name-input"
                required
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                name="lastName"
                data-testid="judge-last-name-input"
                required
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            <div>
              <DatePicker
                key={editing ? editing.id : 'new'}
                label="Date of Birth"
                value={formData.dob}
                onChange={(date) => setFormData(prev => ({ ...prev, dob: date }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="bgNumber">BG Number *</Label>
              <Input
                id="bgNumber"
                name="bgNumber"
                data-testid="judge-bg-number-input"
                required
                value={formData.bgNumber}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="level">Level *</Label>
              <Select value={formData.level} onValueChange={handleLevelChange} required>
                <SelectTrigger data-testid="judge-level-select">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {JUDGE_LEVELS.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="phone">Contact Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                data-testid="judge-phone-input"
                required
                placeholder="e.g., 07123 456789"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="judge-email-input"
                required
                placeholder="e.g., judge@example.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-judge-button"
                className="flex-1 bg-[#000000] hover:bg-[#333333]"
              >
                {editing ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Judge</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {judgeToDelete?.firstName} {judgeToDelete?.lastName}'s profile? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-gray-900 hover:bg-black"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Missing Contact Info Alert Dialog */}
      <AlertDialog open={showMissingInfoDialog} onOpenChange={setShowMissingInfoDialog}>
        <AlertDialogContent className="border-black border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">
              Missing Contact Information
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-gray-700">
              <strong>{judgeMissingInfo?.firstName} {judgeMissingInfo?.lastName}</strong> is missing their 
              {!judgeMissingInfo?.phone && !judgeMissingInfo?.email ? ' contact number and email address' : 
               !judgeMissingInfo?.phone ? ' contact number' : ' email address'}. 
              Please update their profile to add this required information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Later</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowMissingInfoDialog(false);
                handleOpenDialog(judgeMissingInfo);
              }}
              className="bg-black hover:bg-gray-800"
            >
              Update Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}